import { useSession } from "@clerk/clerk-react";
import {
  AlertInfo,
  Award,
  Fullscreen,
  Health,
  LocationAlt,
  Stedenbouw,
  Users,
  Wrench,
} from "assets/icons";
import {
  AddressPicker,
  EditableSelectComponent,
  EditableTextareaComponent,
  renderSection,
} from "components/index";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { CustomFieldsSection } from "shared/components/customFields";

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { useResources } from "shared/contexts/resources.context";
import {
  BuildingDTO,
  BuildingType,
  ConstructiveMainMaterial,
  HeritageStatus,
  PermitStatus,
  SewageType,
  BreeamRating,
  BreeamMethod,
  WellRating,
  WellMethod,
  StrategicStatus,
  OwnershipType,
} from "shared/types/building";
import { ContactTags } from "shared/types/contacts";
import { SecondaryLabel } from "shared/types/file";
import { ResourceType } from "shared/types/resource";
import { createPatchObject } from "shared/utils/patch";

export type BuildingInformationProps = {
  buildingData?: BuildingDTO;
  refreshBuilding: () => void;
};

export const BuildingInformation = ({
  buildingData,
  refreshBuilding,
}: BuildingInformationProps) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editedBuilding, setEditedBuilding] = useState<any | null | undefined>(
    null,
  );
  const { session } = useSession();
  const [building, setBuilding] = useState<any | null | undefined>(
    buildingData,
  );
  const { refreshResources } = useResources();
  const [yallyCompanies, setYallyCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/companies/all`,
          { method: "GET" },
          session,
        );

        const companies = await response.json();

        // Archived companies are no longer returned, but a building can still point at
        // one. Keep the current value in the list (marked) so the field does not look
        // empty and a save cannot silently drop the link.
        const currentCompany = buildingData?.company;
        const hasCurrent =
          !currentCompany ||
          (companies || []).some((company: any) => company.id === currentCompany.id);

        setYallyCompanies(
          hasCurrent
            ? companies
            : [...(companies || []), { ...currentCompany, name: `${currentCompany.name} (gearchiveerd)` }],
        );
      } catch (error) {
        console.error("Failed to load Yally companies: ", error);
        toast.error("Failed to load Yally companies");
      }
    };

    fetchCompanies();
  }, [buildingData?.company?.id]);

  useEffect(() => {
    setIsLoading(false);
    setBuilding(buildingData);
  }, [buildingData]);

  useEffect(() => {
    // Listener for Escape key to exit edit mode
    const handleKeydown = (e: KeyboardEvent) => {
      if (editMode && e.key === "Escape") {
        setEditedBuilding(building);
        toggleEditMode();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => {
      // Cleanup event listener
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [building, editMode]);
  const saveBuildingUpdates = async () => {
    if (!editedBuilding.id || editedBuilding.id === "") {
      console.log(editedBuilding);
      toast.error("Geen gebouwid aanwezig");
      return;
    }

    if (!editedBuilding.company) {
      console.log(editedBuilding);
      toast.error("Duid aan in welke Yally vennootschap dit gebouw zit");
      return;
    }

    const updatedBuilding = {
      ...editedBuilding,
    };

    const patchObject = createPatchObject(building, updatedBuilding);

    if (Object.keys(patchObject).length === 0) {
      toast("No changes detected!");
      return;
    }

    try {
      if (building?.id) {
        // Updating an existing building
        await fetchWithAuth(
          `${API_URL}/building/update/${building?.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patchObject),
          },
          session,
        );
        refreshBuilding();
        toast("Gebouw geupdate!");
      } else {
        // Creating a new building
        const response = await fetchWithAuth(
          `${API_URL}/building`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedBuilding),
          },
          session,
        );
        const newBuildingData = await response.json();
        // Update the building data with the new data
        if (newBuildingData && newBuildingData.id) {
          setBuilding(newBuildingData);
          setEditedBuilding(newBuildingData);
        }
      }

      refreshResources();
    } catch (error) {
      console.error("Failed to save building: ", error);
      toast("Error saving building!");
    } finally {
      setEditMode(false);
      navigate(`/building/${building?.id}`);
    }
  };

  const toggleEditMode = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedBuilding(building);
    }
  };

  const locationFieldsConfig = [
    { label: "Gebouwcode", key: "id", disableEditMode: true },
    { label: "Odoo ID (= Kostenplaatsplan)", key: "odooId" },
    {
      label: "Bedrijf",
      key: "company",
      Component: () => (
        <EditableSelectComponent
          label="Onder welke Yally vennootschap valt dit gebouw?"
          editMode={editMode}
          value={editedBuilding?.company?.id || building?.company?.id || ""}
          options={
            yallyCompanies &&
            yallyCompanies.map((company: any) => ({
              value: company.id,
              label: company.name,
            }))
          }
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              company: { id: value },
            }))
          }
        />
      ),
    },
    {
      label: "Adres",
      Component: () => (
        <div className={editMode ? "grow p-2" : ""}>
          <AddressPicker
            editMode={false}
            defaultAddress={
              building?.googlePlaceLongAddress ||
              editedBuilding?.googlePlaceLongAddress ||
              ""
            }
          />
        </div>
      ),
    },
    { label: "Gewest", key: "region" },
    { label: "Provincie", key: "province" },
    {
      label: "Mobiscore",
      link: "https://mobiscore.omgeving.vlaanderen.be/",
      linkTooltip: "Bereken mobiscore",
      key: "mobiscore",
    },
    {
      label: "Parkeermogelijkheden",
      key: "parkingPlaceNearBuilding",
      Component: EditableTextareaComponent,
    },
    {
      label: "Beschrijving",
      key: "description",
      Component: EditableTextareaComponent,
    },
  ];

  const dimensionsFieldsConfig = [
    {
      label: "Netto gebouwoppervlakte (incl. gemene delen)",
      key: "nettoSurfaceInclCommonAreas",
      mask: "surface",
    },
    {
      label: "Netto gebouwoppervlakte (excl. gemene delen)",
      key: "nettoSurfaceExclCommonAreas",
      mask: "surface",
    },
    { label: "Perceel oppervlakte", key: "plotSurface", mask: "surface" },
    { label: "Perceel diepte", key: "plotDepth", mask: "meters" },
    { label: "Perceel breedte", key: "plotWidth", mask: "meters" },
  ];

  const generalFieldsConfig = [
    {
      label: "Strategische status",
      key: "strategicStatus",
      Component: () => (
        <EditableSelectComponent
          label="Strategische status"
          editMode={editMode}
          value={
            editedBuilding?.strategicStatus ||
            building?.strategicStatus ||
            StrategicStatus.StrategischBehouden
          }
          options={strategicStatusOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              strategicStatus: value,
            }))
          }
        />
      ),
    },
    {
      label: "Eigendomstype",
      key: "ownershipType",
      Component: () => (
        <EditableSelectComponent
          label="Eigendomstype"
          editMode={editMode}
          value={
            editedBuilding?.ownershipType ||
            building?.ownershipType ||
            OwnershipType.Eigendom
          }
          options={ownershipTypeOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              ownershipType: value,
            }))
          }
        />
      ),
    },
    {
      label: "Datum verwerving",
      key: "acquisitionDate",
      Component: () => (
        <DatePicker
          disabled={!editMode}
          placeholderText="Selecteer de datum van verwerving"
          selected={
            (editedBuilding?.acquisitionDate &&
              new Date(editedBuilding?.acquisitionDate)) ||
            (building?.acquisitionDate && new Date(building?.acquisitionDate))
          }
          onChange={(date: any) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              acquisitionDate: date,
            }))
          }
          dateFormat="dd/MM/yyyy"
          className="w-full rounded-sm bg-white p-2.5 text-right text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
        />
      ),
    },
    { label: "Aankoopprijs", key: "acquisitionPrice", mask: "valuta" },
    {
      label: "Gekocht als beroepskoper",
      key: "purchasedAsProfessionalBuyer",
      Component: () => (
        <EditableSelectComponent
          label="Gekocht als beroepskoper?"
          editMode={editMode}
          value={
            editedBuilding?.purchasedAsProfessionalBuyer ||
            building?.purchasedAsProfessionalBuyer ||
            ""
          }
          options={yesNoOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              purchasedAsProfessionalBuyer: value,
            }))
          }
        />
      ),
    },
    { label: "Beroepskoper nabetalingen", key: "backPayments" },
    {
      label: "Beroepskoper nabetalingen deadline",
      key: "deadlineBackPayments",
      Component: () => (
        <DatePicker
          disabled={!editMode}
          placeholderText="Selecteer de deadline voor nabetalingen beroepsverkoper"
          selected={
            (editedBuilding?.deadlineBackPayments &&
              new Date(editedBuilding?.deadlineBackPayments)) ||
            (building?.deadlineBackPayments &&
              new Date(building?.deadlineBackPayments))
          }
          onChange={(date: any) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              deadlineBackPayments: date,
            }))
          }
          dateFormat="dd/MM/yyyy"
          className="w-full rounded-sm bg-white p-2.5  text-right text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
        />
      ),
    },
  ];

  const buildingTypeOptions = Object.values(BuildingType).map((value) => ({
    value: value,
    label: value,
  }));

  const sewageTypeOptions = Object.values(SewageType).map((value) => ({
    value: value,
    label: value,
  }));

  const constructiveMainMaterialOptions = Object.values(
    ConstructiveMainMaterial,
  ).map((value) => ({
    value: value,
    label: value,
  }));

  const permitStatusOptions = Object.values(PermitStatus).map((value) => ({
    value: value,
    label: value,
  }));

  const heritageStatusOptions = Object.values(HeritageStatus).map((value) => ({
    value: value,
    label: value,
  }));

  const breeamRatingOptions = Object.values(BreeamRating).map((value) => ({
    value: value,
    label: value,
  }));

  const breeamMethodOptions = Object.values(BreeamMethod).map((value) => ({
    value: value,
    label: value,
  }));

  const wellRatingOptions = Object.values(WellRating).map((value) => ({
    value: value,
    label: value,
  }));

  const wellMethodOptions = Object.values(WellMethod).map((value) => ({
    value: value,
    label: value,
  }));

  const strategicStatusOptions = Object.values(StrategicStatus).map((value) => ({
    value: value,
    label: value,
  }));

  const ownershipTypeOptions = Object.values(OwnershipType).map((value) => ({
    value: value,
    label: value,
  }));

  const yesNoOptions = [
    { label: "Ja", value: true },
    { label: "Nee", value: false },
    { label: "Niet ingevuld", value: "" },
  ];

  const constructionFieldsConfig = [
    {
      label: "Gebouw Type",
      key: "buildingType",
      Component: () => (
        <EditableSelectComponent
          label="Gebouw type"
          editMode={editMode}
          value={editedBuilding?.buildingType || building?.buildingType || ""}
          options={buildingTypeOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({ ...prev, buildingType: value }))
          }
        />
      ),
    },
    { label: "Bouwlagen", key: "amountOfFloors" },
    { label: "Bouwjaar", key: "constructionYear" },
    {
      label: "Scheidingsstructuur verdiepingen",
      key: "constructiveMainMaterial",
      Component: () => (
        <EditableSelectComponent
          label="Scheidingsstructuur verdiepingen"
          editMode={editMode}
          value={
            editedBuilding?.constructiveMainMaterial ||
            building?.constructiveMainMaterial ||
            ""
          }
          options={constructiveMainMaterialOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              constructiveMainMaterial: value,
            }))
          }
        />
      ),
    },
    {
      label: "Riolering",
      key: "sewageType",
      Component: () => (
        <EditableSelectComponent
          label="Riolering"
          editMode={editMode}
          value={editedBuilding?.sewageType || building?.sewageType || ""}
          options={sewageTypeOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({ ...prev, sewageType: value }))
          }
        />
      ),
    },
    // ... other field configs
  ];

  const permitFieldsConfig = [
    {
      label: "Gebouw vergunningsstatus",
      key: "permitStatus",
      Component: () => (
        <EditableSelectComponent
          label="Gebouw vergunningsstatus"
          editMode={editMode}
          value={editedBuilding?.permitStatus || building?.permitStatus || ""}
          options={permitStatusOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({ ...prev, permitStatus: value }))
          }
        />
      ),
    },
    {
      label: "Gebouw indeling vergund",
      key: "layoutPermitStatus",
      Component: () => (
        <EditableSelectComponent
          label="Gebouw indeling vergund"
          editMode={editMode}
          value={
            editedBuilding?.layoutPermitStatus ||
            building?.layoutPermitStatus ||
            ""
          }
          options={permitStatusOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              layoutPermitStatus: value,
            }))
          }
        />
      ),
    },
    {
      label: "Conformiteitsattest",
      key: "conformityAttest",
      Component: () => (
        <EditableSelectComponent
          label="Conformiteitsattest"
          editMode={editMode}
          value={
            editedBuilding?.conformityAttest || building?.conformityAttest || ""
          }
          options={yesNoOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              conformityAttest: value,
            }))
          }
        />
      ),
    },
    { label: "Aantal units met vergunning", key: "numUnitsWithPermit" },
    { label: "Aantal units zonder vergunning", key: "numUnitsWithoutPermit" },
    {
      label: "Erfgoedstatus",
      key: "heritageStatus",
      Component: () => (
        <EditableSelectComponent
          label="Erfgoedstatus"
          editMode={editMode}
          value={
            editedBuilding?.heritageStatus || building?.heritageStatus || ""
          }
          options={heritageStatusOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              heritageStatus: value,
            }))
          }
        />
      ),
    },
  ];

  const breeamFieldsConfig = [
    {
      label: "BREEAM Rating",
      key: "breeamRating",
      Component: () => (
        <EditableSelectComponent
          label="BREEAM Rating"
          editMode={editMode}
          value={editedBuilding?.breeamRating || building?.breeamRating || ""}
          options={breeamRatingOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              breeamRating: value,
            }))
          }
        />
      ),
    },
    {
      label: "BREEAM Methode",
      key: "breeamMethod",
      Component: () => (
        <EditableSelectComponent
          label="BREEAM Methode"
          editMode={editMode}
          value={editedBuilding?.breeamMethod || building?.breeamMethod || ""}
          options={breeamMethodOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              breeamMethod: value,
            }))
          }
        />
      ),
    },
    {
      label: "BREEAM Score (%)",
      key: "breeamScorePercent",
      mask: "percentage",
      Component: () => (
        <div className={editMode ? "grow p-2" : ""}>
          {editMode ? (
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                defaultValue={editedBuilding?.breeamScorePercent ?? building?.breeamScorePercent ?? ""}
                onBlur={(e) => {
                  const value = parseFloat(e.target.value);
                  if (e.target.value === "" || isNaN(value)) {
                    setEditedBuilding((prev: any) => ({
                      ...prev,
                      breeamScorePercent: null,
                    }));
                  } else {
                    const clampedValue = Math.min(100, Math.max(0, value));
                    e.target.value = String(clampedValue);
                    setEditedBuilding((prev: any) => ({
                      ...prev,
                      breeamScorePercent: clampedValue,
                    }));
                  }
                }}
                placeholder="0 - 100"
                className="w-full rounded-sm bg-white p-2.5 pr-8 text-right text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-secondary-mid">%</span>
            </div>
          ) : (
            <p className="font-medium">
              {building?.breeamScorePercent != null
                ? `${building.breeamScorePercent} %`
                : "Niet ingevuld"}
            </p>
          )}
        </div>
      ),
    },
    {
      label: "BREEAM Versie",
      key: "breeamVersion",
    },
    {
      label: "BREEAM Certificaat ID",
      key: "breeamCertificateId",
    },
  ];

  const wellFieldsConfig = [
    {
      label: "WELL Rating",
      key: "wellRating",
      Component: () => (
        <EditableSelectComponent
          label="WELL Rating"
          editMode={editMode}
          value={editedBuilding?.wellRating || building?.wellRating || ""}
          options={wellRatingOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              wellRating: value,
            }))
          }
        />
      ),
    },
    {
      label: "WELL Methode",
      key: "wellMethod",
      Component: () => (
        <EditableSelectComponent
          label="WELL Methode"
          editMode={editMode}
          value={editedBuilding?.wellMethod || building?.wellMethod || ""}
          options={wellMethodOptions}
          onChange={(value: string) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              wellMethod: value,
            }))
          }
        />
      ),
    },
    {
      label: "WELL Score (punten)",
      key: "wellScorePoints",
      mask: "points",
      Component: () => (
        <div className={editMode ? "grow p-2" : ""}>
          {editMode ? (
            <div className="relative">
              <input
                type="number"
                min="0"
                max="110"
                step="1"
                defaultValue={editedBuilding?.wellScorePoints ?? building?.wellScorePoints ?? ""}
                onBlur={(e) => {
                  const value = parseFloat(e.target.value);
                  if (e.target.value === "" || isNaN(value)) {
                    setEditedBuilding((prev: any) => ({
                      ...prev,
                      wellScorePoints: null,
                    }));
                  } else {
                    const clampedValue = Math.min(110, Math.max(0, value));
                    e.target.value = String(clampedValue);
                    setEditedBuilding((prev: any) => ({
                      ...prev,
                      wellScorePoints: clampedValue,
                    }));
                  }
                }}
                placeholder="0 - 110"
                className="w-full rounded-sm bg-white p-2.5 pr-16 text-right text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-secondary-mid">punten</span>
            </div>
          ) : (
            <p className="font-medium">
              {building?.wellScorePoints != null
                ? `${building.wellScorePoints} punten`
                : "Niet ingevuld"}
            </p>
          )}
        </div>
      ),
    },
    {
      label: "WELL Certificaat ID",
      key: "wellCertificateId",
    },
    {
      label: "WELL Geldig vanaf",
      key: "wellValidFrom",
      Component: () => (
        <DatePicker
          disabled={!editMode}
          placeholderText="Selecteer de startdatum"
          selected={
            (editedBuilding?.wellValidFrom &&
              new Date(editedBuilding?.wellValidFrom)) ||
            (building?.wellValidFrom && new Date(building?.wellValidFrom))
          }
          onChange={(date: any) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              wellValidFrom: date,
            }))
          }
          dateFormat="dd/MM/yyyy"
          className="w-full rounded-sm bg-white p-2.5 text-right text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
        />
      ),
    },
    {
      label: "WELL Geldig tot",
      key: "wellValidTo",
      Component: () => (
        <DatePicker
          disabled={!editMode}
          placeholderText="Selecteer de einddatum"
          selected={
            (editedBuilding?.wellValidTo &&
              new Date(editedBuilding?.wellValidTo)) ||
            (building?.wellValidTo && new Date(building?.wellValidTo))
          }
          onChange={(date: any) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              wellValidTo: date,
            }))
          }
          dateFormat="dd/MM/yyyy"
          className="w-full rounded-sm bg-white p-2.5 text-right text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
        />
      ),
    },
  ];

  const createContactFieldComponent = (
    label: any,
    key: any,
    resourceFilter: any,
    metadataFilter: any,
  ) => {
    return () => (
      <div className={editMode ? "grow p-2" : ""}>
        <SearchComponent
          type="regular"
          label={label}
          resourceFilter={resourceFilter}
          metadataFilter={metadataFilter}
          initialValue={
            (building as any)?.[key]?.name ||
            (editedBuilding as any)?.[key]?.name
          }
          onSelect={(resource) =>
            setEditedBuilding((prev: any) => ({
              ...prev,
              [key]: {
                id: resource.resourceId,
                name: resource.name,
                type: resource.type,
              },
            }))
          }
          mode={SearchMode.SEARCH_AND_PRESENT}
          editMode={editMode}
        />
      </div>
    );
  };

  const contactsFieldData = [
    {
      label: "Notaris",
      key: "notary",
      filterLabel: "Zoek de notaris in de lijst van notarissen",
      metadataFilter: ContactTags.Notaris,
    },
    { label: "Verkoper", key: "seller", filterLabel: "Zoek de verkoper" },
    {
      label: "Bij welke partij is het gebouw verzekerd?",
      key: "insurance",
      filterLabel: "Zoek een verzekeraar",
      metadataFilter: ContactTags.Verzekeringsmaatschappij,
    },
    {
      label: "Wie heeft de Tech DD uitgevoerd op het pand?",
      key: "techDD",
      filterLabel: "Tech DD",
    },
    {
      label: "Wie heeft de Legal DD uitgevoerd op het pand?",
      key: "legalDD",
      filterLabel: "Legal DD",
    },
    {
      label: "Wie heeft de Energy Audit uitgevoerd op het pand?",
      key: "energyAudit",
      filterLabel: "Energy Audit",
    },
  ];

  const contactsFieldsConfig = contactsFieldData.map(
    ({ label, key, filterLabel, metadataFilter }) => ({
      label: label,
      key: key,
      Component: createContactFieldComponent(
        filterLabel,
        key,
        [ResourceType.COMPANY, ResourceType.CONTACT],
        metadataFilter,
      ),
    }),
  );

  const sharedSectionProps = {
    editMode,
    isLoading,
    onEditClick: toggleEditMode,
    onSaveClick: saveBuildingUpdates,
    editedState: editedBuilding,
    originalState: building,
    setStateFunction: setEditedBuilding,
  };

  return (
    yallyCompanies &&
    building && (
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <div>
          {renderSection({
            title: "Locatie",
            icon: <LocationAlt />,
            fieldsConfig: locationFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          <ImagesContainer
            buildingId={building && building.id}
            secondaryLabel={SecondaryLabel.COMMERCIEEL}
          />
          {/* <ImagesGallery incomingBuildingId={building && building.id} incomingFileLabel={FileLabel.AcquisitionImages} /> */}
        </div>
        <div>
          {renderSection({
            title: "Algemeen",
            icon: <AlertInfo />,
            fieldsConfig: generalFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Afmetingen",
            icon: <Fullscreen />,
            fieldsConfig: dimensionsFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Stedenbouw",
            icon: <Stedenbouw />,
            fieldsConfig: permitFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Bouw",
            icon: <Wrench />,
            fieldsConfig: constructionFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "BREEAM Certificering",
            icon: <Award />,
            fieldsConfig: breeamFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "WELL Certificering",
            icon: <Health />,
            fieldsConfig: wellFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Contacten",
            icon: <Users />,
            fieldsConfig: contactsFieldsConfig,
            ...sharedSectionProps,
          })}
        </div>
        <div className="col-span-full" data-tour="custom-fields-section">
          <CustomFieldsSection scope="building" entityId={building?.id} />
        </div>
      </div>
    )
  );
};
